import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import EditableSection from '../../../components/EditableSection';
import Toggle from '../../../components/ui/Toggle';
import { useEditableDraft } from '../../../hooks/useEditableDraft';
import Permission from '../../../common/permissions';
import { makeHasPermissionSelector } from '../../../selector/authenticationSelector';
import {
    WindowPauseSettings,
    useGetWindowPauseQuery,
    useUpdateWindowPauseMutation,
} from '../../../api/group/groupAdminApiSlice';
import { useGetHolidayStatusQuery } from '../../../api/account/accountApiSlice';
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from '../../../action/snackbarCommands';
import { errorMessageFrom } from '../../../utils/errorMessage';

/** Both switches are on until the room says otherwise (the columns' default). */
const DEFAULTS: WindowPauseSettings = { school: true, public: true };

/**
 * Whether this room's allocation windows pause on school holidays and on
 * public holidays of the tenant's region (D7, M9 × M4; PO 2026-10-24 Q5 A).
 * A paused date is free time for anyone; bookings already on it stay (Q1 A).
 *
 * One editable unit beside the room's windows (design-decisions › Bearbeiten
 * und Speichern). Without a holiday region nothing pauses, and the unit says
 * so rather than suggesting the switches act.
 */
const WindowPauseSection: React.FC<{ groupId: string }> = ({ groupId }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const canWrite = useSelector(makeHasPermissionSelector(Permission.writeCustomers()));
    const { data, isError, isLoading } = useGetWindowPauseQuery({ groupId });
    const { data: holidayStatus } = useGetHolidayStatusQuery();
    const [updateWindowPause, { isLoading: saving }] = useUpdateWindowPauseMutation();
    const saved = data ?? DEFAULTS;
    const draft = useEditableDraft<WindowPauseSettings>(saved);
    const noRegion = holidayStatus !== undefined && holidayStatus.region === null;

    const p = (key: string) => t(`allocation_windows.pause.${key}`);

    const handleSave = async () => {
        try {
            await updateWindowPause({ groupId, ...draft.value }).unwrap();
            dispatch(enqueueSuccessSnackbar({ message: p('saved'), autoHideDuration: 3000 }));
            draft.finish();
        } catch (err) {
            dispatch(enqueueErrorSnackbar({ message: errorMessageFrom(err, p('save_error'), t), autoHideDuration: 5000 }));
        }
    };

    const stateOf = (on: boolean) => p(on ? 'pauses' : 'holds');
    const view = (() => {
        if (isLoading) return <p className={'text-sm text-foreground/60'}>{t('common.loading')}</p>;
        if (isError) return <p className={'text-sm text-foreground'}>{p('load_error')}</p>;
        return (
            <div className={'flex flex-col gap-1 text-sm text-foreground'}>
                <p>{`${p('school')}: ${stateOf(draft.value.school)}`}</p>
                <p>{`${p('public')}: ${stateOf(draft.value.public)}`}</p>
                {noRegion && <p className={'text-foreground/60'}>{p('no_region')}</p>}
            </div>
        );
    })();

    return (
        <EditableSection
            id={'window-pause'}
            title={p('title')}
            description={p('description')}
            readOnly={!canWrite || isError || isLoading}
            editing={draft.editing}
            isDirty={draft.isDirty}
            saving={saving}
            onEdit={draft.startEdit}
            onCancel={draft.cancel}
            onSave={() => { void handleSave(); }}
            view={view}
        >
            <div className={'flex flex-col gap-3'}>
                <Toggle
                    label={p('school')}
                    checked={draft.value.school}
                    onChange={(school) => draft.setDraft({ ...draft.value, school })}
                    disabled={saving}
                />
                <Toggle
                    label={p('public')}
                    checked={draft.value.public}
                    onChange={(publicHolidays) => draft.setDraft({ ...draft.value, public: publicHolidays })}
                    disabled={saving}
                />
                {noRegion && <p className={'text-sm text-foreground/60'}>{p('no_region')}</p>}
            </div>
        </EditableSection>
    );
};

export default WindowPauseSection;
